import type { ColumnType } from 'kysely';

export type Generated<T> =
    T extends ColumnType<infer S, infer I, infer U>
        ? ColumnType<S, I | undefined, U>
        : ColumnType<T, T | undefined, T>;
export type Timestamp = ColumnType<Date, Date | string, Date | string>;

export const SrPartialPackingTypeMap = {
    ALWAYS_ROUND_DOWN: 'ALWAYS_ROUND_DOWN',
    ALWAYS_ROUND_UP: 'ALWAYS_ROUND_UP',
    ONLY_ROUND_DOWN: 'ONLY_ROUND_DOWN',
    ONLY_ROUND_UP: 'ONLY_ROUND_UP',
    NO_PACKAGE_BREAK: 'NO_PACKAGE_BREAK',
    UNKNOWN: 'UNKNOWN',
    NO_RESTRICTION: 'NO_RESTRICTION',
} as const;

export type SrPartialPackingTypeMap = (typeof SrPartialPackingTypeMap)[keyof typeof SrPartialPackingTypeMap];

export const SrSalesOrderType = {
    ORDER: 'ORDER',
    CREDIT_NOTE: 'CREDITNOTE',
    QUOTE: 'QUOTE',
} as const;

export type SrSalesOrderType = (typeof SrSalesOrderType)[keyof typeof SrSalesOrderType];

export const SrOrderStatus = {
    OPEN: 'OPEN',
    PARTIALLY_BILLED: 'PARTIALLY_BILLED',
    BILLED: 'BILLED',
    OPEN_IN_PICKING: 'OPEN_IN_PICKING',
    PROCESSED_INTERNALLY: 'PROCESSED_INTERNALLY',
    CANCELLED: 'CANCELLED',
    PARTIALLY_RECEIVED: 'PARTIALLY_RECEIVED',
    RECEIVED: 'RECEIVED',
    LOST: 'LOST',
    REVISED: 'REVISED',
    COMPLETED: 'COMPLETED',
    UNKNOWN: 'UNKNOWN',
    RELEASED_FOR_DELIVERY: 'RELEASED_FOR_DELIVERY',
    RETURNED: 'RETURNED',
    SHIPPED: 'SHIPPED',
    PARTIALLY_PICKED: 'PARTIALLY_PICKED',
    PICKED: 'PICKED',
    PARTIALLY_SHIPPED: 'PARTIALLY_SHIPPED',
    PENDING: 'PENDING',
    OTHER: 'OTHER',
} as const;

export type SrOrderStatus = (typeof SrOrderStatus)[keyof typeof SrOrderStatus];

export const SrOrderLineStatus = {
    OPEN: 'OPEN',
    OPEN_IN_PICKING: 'OPEN_IN_PICKING',
    RELEASED_FOR_DELIVERY: 'RELEASED_FOR_DELIVERY',
    RELEASED_FOR_BILLING: 'RELEASED_FOR_BILLING',
    BILLED: 'BILLED',
    PROCESSED_INTERNALLY: 'PROCESSED_INTERNALLY',
    CANCELLED: 'CANCELLED',
    UNKNOWN: 'UNKNOWN',
    RETURNED: 'RETURNED',
    SHIPPED: 'SHIPPED',
    PARTIALLY_BILLED: 'PARTIALLY_BILLED',
    PARTIALLY_SHIPPED: 'PARTIALLY_SHIPPED',
    PARTIALLY_RECEIVED: 'PARTIALLY_RECEIVED',
    PENDING: 'PENDING',
    RECEIVED: 'RECEIVED',
    LOST: 'LOST',
} as const;

export type SrOrderLineStatus = (typeof SrOrderLineStatus)[keyof typeof SrOrderLineStatus];

export const SrCustomerAddressType = {
    SHIPPING: 'SHIPPING',
    BILLING: 'BILLING',
    PRIMARY: 'PRIMARY',
} as const;

export type SrCustomerAddressType = (typeof SrCustomerAddressType)[keyof typeof SrCustomerAddressType];

export const SrCustomerStatus = {
    NEW: 'new',
    ACTIVE: 'active',
    AT_RISK: 'at_risk',
    INACTIVE: 'inactive',
} as const;

export type SrCustomerStatus = (typeof SrCustomerStatus)[keyof typeof SrCustomerStatus];

export const SrInsightType = {
    CHURN_WARNING: 'ChurnWarningWebCategory',
    CROSS_SELL: 'XSellWebCategory',
    FIRST_PURCHASE: 'FirstPurchaseWebCat3',
    SHARE_OF_WALLET: 'ShareOfWalletCategory',
} as const;

export type SrInsightType = (typeof SrInsightType)[keyof typeof SrInsightType];

export type OrderLineFacts = {
    _order_line_transaction_key: number;
    _customer_key: number;
    _product_variant_key: number;
    _sales_rep_key_1: number;
    _sales_rep_key_2: number;
    _sales_rep_key_3: number;
    _order_date_key: number;
    _order_creation_date_key: number;
    _order_release_date_key: number;
    _order_line_delivery_date_key: number;
    _order_line_invoice_date_key: number;
    _clerk_key: number;
    order_id: string;
    customer_id: string;
    order_currency_id: string;
    order_currency_rate: number;
    order_delivery_address_id: string;
    order_line_delivered_quantity: number;
    order_line_gross_margin: number;
    order_line_line_item_id: string;
    order_line_line_item_status: string;
    order_line_order_quantity: number;
    order_line_ship_from_id: string;
    order_line_status: string;
    order_line_total_amount: number;
    order_line_total_amount_hd: number;
    order_line_unit_price: number;
    order_quote_sales_rep_id: string;
    order_ship_from_id: string;
    order_status: string;
    order_line_product_variant_id: string;
    product_variant_name: string;
};

export type CustomerDimension = {
    customer_id: string;
    customer_class: string | null;
    customer_name: string; // this is nullable in db but never seems to be null
    customer_group_id: string | null;
    address_line_1: string | null;
    address_line_2: string | null;
    address_line_3: string | null;
    street: string | null;
    zip_code: string | null;
    city: string | null;
    country_code: string | null;
    industry_sector: string | null;
    phone_number: string | null;
    mobile_number: string | null;
    fax_number: string | null;
    _customer_key: string;
    company_id: string | null;
    sales_region: string | null;
};

export type DateDimention = {
    _date_key: number;
    date: Date;
    year: number;
    month: number;
    day_of_month: number;
    quarter: number;
    day_of_week: number;
    day_of_year: number;
    week_of_year: number;
    is_weekend: boolean;
};

export type ProductDimention = {
    _product_key: string;
    _seq_key: number;
    product_id: string;
    product_name: string;
    description_short: string;
    description: string;
    updated_at: string;
    _meta_variant_id: string;
};

export type ProductVariantDimention = {
    _product_variant_key: number;
    _seq_key: number;
    product_variant_id: string;
    product_id: string;
    product_variant_name: string;
    status: string;
    is_active: number;
    article_group_id: string | null;
    article_group_name: string | null;
    article_main_group_name: string;
    quantity_unit_storage: string;
    quantity_unit_sales: string | null;
    factor_sales_unit: number;
    retail_price: number;
    gtin: string;
    ean: string;
    price_scale_factor: number;
    package_unit: string | null;
    sales_units_per_package_unit: number | null;
    partial_packing_type: SrPartialPackingTypeMap | null;
    parts_list_type: string;
    supplier_id: string;
    supplier_name: string;
    purchase_order_number: string;
    image_path: string;
    updated_at: string;
};

export type SalesRepDimension = {
    sales_rep_id: string;
    sales_rep_name: string;
    is_internal: number;
    is_inactive: number;
    _sales_rep_key: number;
};

export type AppCustomerAddresses = {
    id: string;
    _seq_key: number;
    is_default: boolean | null;
    type: SrCustomerAddressType | null;
    customer_id: string;
    address_line_1: string;
    address_line_2: string | null;
    address_line_3: string | null;
    post_box: string | null;
    zip_code_post_box: string | null;
    street: string | null;
    zip_code: string;
    city: string | null;
    country_code: string;
    country: string | null;
};

export type AppCustomerContacts = {
    id: string;
    _seq_key: number;
    customer_id: string;
    salutation: string;
    name: string;
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
    mobile_number: string;
    fax_number: string;
};

export type AppProductParts = {
    id: string;
    _seq_key: number;
    part_list_article_id: string;
    contained_article_id: string;
    variant_id: string;
    quantity: string;
    quantity_scale_factor: number;
    quantity_unit_storage: string;
};

export type AppSalesOrders = {
    id: string;
    _seq_key: number;
    sales_rep_id_1: string;
    sales_rep_id_2: string;
    sales_rep_id_3: string;
    customer_id: string;
    contact_id: string;
    customer_contact_name: string;
    storage_id: string;
    type: string;
    sales_order_type: string;
    status: string;
    clerk_id: string;
    clerk_id_entry: string;
    order_text: string;
    entry_date: string;
    order_date: string;
    shipping_type_id: string;
    delivery_type_id: string;
    delivery_address_id: string;
    delivery_address_line_1: string;
    delivery_address_line_2: string;
    delivery_address_line_3: string;
    delivery_address_street: string;
    delivery_address_zip_code: string;
    delivery_address_city: string;
    delivery_address_country_code: string;
    billing_address_line_1: string;
    billing_address_line_2: string;
    billing_address_line_3: string;
    billing_address_street: string;
    billing_address_post_box: string;
    billing_address_zip_post_box: string;
    billing_address_zip_code: string;
    billing_address_city: string;
    billing_address_country_code: string;
    billing_address_country: string;
    updated_at: string;
    po_number: string;
};

export type AppSalesOrdersMV = {
    id: string;
    clerk_id?: string;
    clerk_name?: string;
    customer_id: string;
    customer_name: string;
    company_id: string;
    company_name: string;
    display_id: string;
    display_identifier: string;
    document_type_name?: string;
    document_type?: string;
    entry_date: string;
    line_items_count: string;
    order_date: string;
    po_number: string;
    sales_order_id: string;
    sales_rep_ids: string;
    status: string;
    storage_id: string;
    total_amount_net: string;
    total_margin: string;
    type: string;
};

export type AppSalesReps = {
    id: string;
    _seq_key: number;
    name: string;
    is_internal: number;
    is_inactive: number;
};

export type AppSalesRepCustomers = {
    id: string;
    _seq_key: number;
    customer_id: string;
    sales_rep_id: string;
    type: string;
};

export type AppShippingTypes = {
    id: string;
    _seq_key: number;
    name: string;
    dispatcher: string;
};

export type AppStorageGroups = {
    id: string;
    _seq_key: number;
    name: string;
};

export type AppStorageLocations = {
    id: string;
    _seq_key: number;
    group_id: string;
    name: string;
    address_line_1: string;
    address_line_2: string;
    address_line_3: string;
    street: string;
    zip_code: number;
    city: string;
    country_code: string;
};

export type AppStorageStockLevels = {
    id: string;
    _seq_key: number;
    product_variant_id: string;
    storage_id: string;
    stock_quantity: number;
    reserved: number;
    picked: number;
    available: number;
    effective: number;
};

export type CustomerProductCategoryChurnWarningsFacts = {
    _customer_category_churn_warning_key: string;
    customer_id: string;
    product_category_id: string;
    product_category_name: string;
    recency: number;
    days_between_order_threshold_80: number;
    days_between_order_threshold_85: number;
    days_between_order_threshold_90: number;
    days_between_order_threshold_95: number;
    days_between_order_mean: number;
    days_between_order_median: number;
    churn_warning_at_80: number;
    churn_warning_at_85: number;
    churn_warning_at_90: number;
    churn_warning_at_95: number;
    rank: number;
    total_spent_last_year: number;
};

export type AppCategorys = {
    id: string;
    _seq_key: number;
    title: string;
    parent_id: string;
    depth: number;
    breadcrumbs: string;
};

export type AppProductsCategories = {
    id: string;
    _seq_key: number;
    category_id: string;
    product_variant_id: string;
};

export type AppSalesOrderTypes = {
    id: string;
    _seq_key: number;
    type: string;
    name: string;
};

export type AppRepeatPurchasesRecommendation = {
    _customer_repeat_purchase_recommendation_key: string;
    customer_id: number;
    product_id: number;
    rank: number;
    number_of_unique_orders: number;
    last_order_date: string;
    product_variant_id_most_common: string;
    product_variant_id_most_recent: string;
    recency_days: number;
    recency_weeks: string;
    recency_comparison_date: string;
    r2: number;
    shape: number;
    loc: number;
    scale: number;
    mode_days: number;
    RAi: number;
    QAi: number;
    PAi: number;
};

export type AppInsight = {
    id: string;
    insight_type: SrInsightType;
    grouping_entity_id: string;
    grouping_entity_type: string;
    actionable_entity_id: string;
    actionable_entity_type: string;
    absolute_number: number;
    percentage_number: number;
    reason: string;
    generated_at: string;
    reason_locale: string;
    batch_id: string;
    template_arguments_json: string;
    rank: number;
    aggregated_at: string;
    aggregated_id: string;
};

export type AppUsedInsight = {
    insight_id: string;
    reaction?: string | null;
    used_at?: string | null;
    hidden_at?: string | null;
    notes?: string | null;
    reason?: string | null;
};

export type ClerkDimension = {
    _clerk_key: string;
    clerk_id: string;
    email: string;
    first_name: string;
    last_name: string;
    phone_number: string;
    full_name: string;
};

export type CompanyDimension = {
    _company_key: string;
    company_id: string | null;
    company_name: string | null;
};

export type ProductRankingsByyIndustryAndCategoryFacts = {
    customer_segment_id: string;
    customer_segment_column: string;
    product_variant_id: string;
    product_variant_name: string;
    product_category_id: string;
    product_category_name: string;
    order_count: number;
    total_revenue: number;
    ranking_method: string;
    rank: number;
};

export type PersonalisedInsightItemRecommendations = {
    insight_id: string;
    product_variant_id: string;
    aggregated_id: string;
    insight_type: string;
    grouping_entity_id: string;
    actionable_entity_id: string;
    recommendation_source: string;
    product_variant_name: string;
    rank: number;
    source_batch_id: string;
    score: number;
    aggregated_at: string;
};

export type AppProductsCategoriesBridge = {
    product_variant_id: string;
    product_category_id: string;
    parent_product_category_id: string;
    product_category_name: string;
    depth: number;
    depth_from_parent: number;
    parent_product_category_name: string;
};

export type RelatedItemRecommendation = {
    product_variant_id: string;
    recommended_product_variant_id: string;
    rank: number;
    score: number;
    source: string;
    created_at: string;
};

export type ChurnWarningsProducts = {
    id: string;
    customer_id: string;
    product_category_id: string;
    product_variant_id: string;
    batch_id: string;
    generated_at: string;
    display_product: number;
};

export type PlatoStatus = {
    customer_id: string;
    days_since_first_order: number | null;
    days_since_last_order: number | null;
    revenue_730_365: number | null;
    revenue_365_0: number | null;
    order_count_730_365: number | null;
    order_count_365_0: number | null;
    churn: number | null;
    year_to_year_revenue: number | null;
    plato_status: SrCustomerStatus | null;
};

export type OrderAndInvoiceLineDailyRevenue = {
    date: string | null;
    _date_key: number;
    order_line_status: string;
    article_group_id: string;
    article_group_name: string;
    clerk_id: string;
    clerk_fullname: string;
    customer_id: string;
    customer_name: string;
    industry_sector: string | null;
    order_type: string;
    product_variant_id: string;
    product_variant_name: string;
    sales_rep_ids: string;
    storage_location_id: string;
    storage_location_name: string;
    supplier_id: string;
    supplier_name: string;
    metric_type: string; // 'BILLED' | 'UNBILLED'
    daily_revenue: number;
    daily_gross_margin: number;
    daily_units: number;
};

export type InvoiceLineFacts = {
    _transaction_key: string;
    _sales_order_item_key: string;
    _customer_key: string;
    _product_variant_key: string;
    _clerk_key: string;
    _order_date_key: string;
    _invoice_line_date_key: string;
    _order_creation_date_key: string;
    _order_release_date_key: string;
    customer_id: string;
    order_id: string;
    order_line_id: string;
    order_status: string;
    order_line_status: string;
    order_currency_id: string;
    order_currency_rate: number;
    order_line_product_variant_id: string;
    product_variant_name: string;
    order_line_order_quantity: number;
    order_line_unit_price: number;
    total_amount: number;
    total_amount_hd: number;
    gross_margin: number;
    gross_margin_hd: number;
    transaction_type: string;
};

export type CurrencyDimension = {
    _currency_key: string;
    currency_id: string;
    currency_iso_code: string;
    currency_name: string;
    is_default: 0 | 1;
};

export type WithoutMeta<T> = Omit<T, `_${string}`>;

export type DB = {
    product_rankings_by_industry_and_category_facts: ProductRankingsByyIndustryAndCategoryFacts;
    personalised_insight_item_recommendations: PersonalisedInsightItemRecommendations;
    customer_product_category_churn_warnings_facts: CustomerProductCategoryChurnWarningsFacts;
    order_line_transaction_facts: OrderLineFacts;
    invoice_line_transaction_facts: InvoiceLineFacts;
    customer_dim: CustomerDimension;
    clerk_dim: ClerkDimension;
    company_dim: CompanyDimension;
    date_dim: DateDimention;
    product_dim: ProductDimention;
    product_variants_dim: ProductVariantDimention;
    sales_representative_dim: SalesRepDimension;
    currency_dim: CurrencyDimension;
    categories: AppCategorys;
    products_categories: AppProductsCategories;
    customer_addresses: AppCustomerAddresses;
    customer_contacts: AppCustomerContacts;
    product_parts: AppProductParts;
    sales_orders: AppSalesOrders;
    sales_reps: AppSalesReps;
    sales_reps_customers: AppSalesRepCustomers;
    shipping_types: AppShippingTypes;
    storage_groups: AppStorageGroups;
    storage_locations: AppStorageLocations;
    storage_stock_levels: AppStorageStockLevels;
    sales_order_types: AppSalesOrderTypes;
    repeat_purchases_recommendations: AppRepeatPurchasesRecommendation;
    ins__insight: AppInsight;
    ins__used_insight: AppUsedInsight;
    product_web_category_bridge: AppProductsCategoriesBridge;
    related_item_recommendations: RelatedItemRecommendation;
    churn_warnings_products: ChurnWarningsProducts;
    plato_status: PlatoStatus;
    sales_order_mv: AppSalesOrdersMV;
    order_and_invoice_line_daily_revenue: OrderAndInvoiceLineDailyRevenue;
};
